import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { AccountInfo } from "@azure/msal-browser";
import { AuthContext } from "@/context/AuthContext";
import { msalInstance } from "@/utils/auth-config";
import { AppRoutes } from "@/constants/routes";

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AccountInfo | null>(null);

  useEffect(() => {
    const checkIfLoggedIn = async () => {
      const savedTokens = localStorage.getItem("msalToken");
      const savedUsers = JSON.parse(localStorage.getItem("msalUser") ?? "{}");
      const savedToken = savedTokens ?? null;
      const savedUser = savedUsers ?? null;

      if (savedToken && savedUser) {
        await msalInstance.initialize();
        
        // Limpiar cualquier interacción pendiente
        await msalInstance.handleRedirectPromise().catch(() => {});
        
        msalInstance.setActiveAccount(savedUser);
        setUser(savedUser);

        try {
          const response = await msalInstance.acquireTokenSilent({
            account: msalInstance.getActiveAccount() ?? undefined,
            scopes: ["openid", "profile", "User.Read", "email"],
          });

          localStorage.setItem("msalToken", response.accessToken);
        } catch (error) {
          console.error("Error al intentar refrescar el token:", error);
          if (globalThis.location?.pathname !== AppRoutes.LOGIN) {
            globalThis.location.href = AppRoutes.LOGIN;
          }
        }
      } else if (globalThis.location?.pathname !== AppRoutes.LOGIN) {
        globalThis.location.href = AppRoutes.LOGIN;
      }
    };

    checkIfLoggedIn();
  }, []);

  const login = async () => {
    await msalInstance.initialize();

    try {
      // Limpiar cualquier interacción pendiente antes de intentar login
      await msalInstance.handleRedirectPromise().catch(() => {});
      
      const response = await msalInstance.loginPopup({
        scopes: ["openid", "profile", "User.Read", "email"],
      });

      if (response.account) {
        setUser(response.account);
        localStorage.setItem("msalToken", response.accessToken);
        localStorage.setItem("msalUser", JSON.stringify(response.account));
        msalInstance.setActiveAccount(response.account);
      }

      return true;
    } catch (error: any) {
      console.error(error);
      
      // Si hay interacción en progreso, limpiar y reintentar después de un momento
      if (error.errorCode === "interaction_in_progress") {
        console.warn("Interacción en progreso detectada, limpiando...");
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Intentar limpiar el estado
        sessionStorage.clear();
        
        // Sugerir al usuario que recargue
        throw new Error("Por favor, recarga la página e intenta nuevamente");
      }
      
      setUser(null);
      localStorage.removeItem("msalToken");
      localStorage.removeItem("msalUser");
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Limpiar localStorage primero
      localStorage.removeItem("msalToken");
      localStorage.removeItem("msalUser");
      setUser(null);
      
      // Limpiar cuentas de MSAL
      msalInstance.setActiveAccount(null);
      
      // Intentar logout popup, pero no fallar si hay error
      await msalInstance.logoutPopup().catch((error) => {
        console.warn("Error en logoutPopup (ignorado):", error);
      });
      
      return true;
    } catch (error) {
      console.error("Error en logout:", error);
      return false;
    }
  };

  // Función para limpiar sesión sin popup (usada por axios interceptor)
  const clearSession = () => {
    localStorage.removeItem("msalToken");
    localStorage.removeItem("msalUser");
    sessionStorage.clear();
    setUser(null);
    msalInstance.setActiveAccount(null);
  };

  const contextValue = useMemo(
    () => ({
      login,
      logout,
      clearSession, // Exponer esta función
      user,
    }),
    [user], // Remover login y logout de las dependencias para evitar recreaciones
  );
  
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};